import { type ComponentPropsWithoutRef, createContext, useContext, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { invariant } from "@pexip-engage/utils";
import { Form } from "@remix-run/react";
import clsx from "clsx";
import type { MeetingType } from "skedify-types";

import { Button, PrimaryButton } from "~/components/Button";
import { LocationIcon } from "~/components/Icons/LocationIcon";
import { OfficeIcon } from "~/components/Icons/OfficeIcon";
import { PhoneIcon } from "~/components/Icons/PhoneIcon";
import { VideoIcon } from "~/components/Icons/VideoIcon";
import { Spinner } from "~/components/Loader";
import { LocationSearch } from "~/components/LocationSearch";
import { Map, useMarkers } from "~/components/Map";
import { Text } from "~/components/Text";
import { usePendingNavigation } from "~/hooks/useShowSpinner";
import type { MeetingTypeLoaderData } from "~/modules/meeting-type/meeting-type.loader.server";
import { useStateManager } from "~/modules/StateManager.hooks";
import type { PlaceDetailLoaderData } from "~/routes/api+/enterprises.$enterprise.place-details";
import type { SetState } from "~/utils/utility.types";

const MeetingTypeContext = createContext<MeetingTypeLoaderData | null>(null);

export const MeetingTypeContextProvider = MeetingTypeContext.Provider;

function useMeetingTypeContext() {
  const context = useContext(MeetingTypeContext);
  invariant(context, "MeetingTypeContext is not defined");

  return context;
}

export function MeetingTypeContainer() {
  const { meetingTypes } = useMeetingTypeContext();

  const [selectedMeetingType, setSelectedMeetingType] = useState(
    meetingTypes.length === 1 ? meetingTypes[0] ?? null : null,
  );

  const showMeetingTypeLocation = selectedMeetingType && selectedMeetingType !== "office";

  return (
    <div className="grid justify-items-center gap-lg md:justify-items-start">
      <div
        className="grid w-full grid-cols-1 gap-sm md:grid-cols-2 lg:grid-cols-4 xl:gap-md"
        data-component="MeetingTypeContainer"
      >
        {meetingTypes.map((meetingType) => (
          <MeetingTypeLink
            key={meetingType}
            meetingType={meetingType}
            selectedMeetingType={selectedMeetingType}
            setSelectedMeetingType={setSelectedMeetingType}
          />
        ))}
      </div>
      {showMeetingTypeLocation && (
        <Form method="post">
          <MeetingTypeLocation meetingType={selectedMeetingType} />
        </Form>
      )}
    </div>
  );
}

interface MeetingTypeLinkProps {
  selectedMeetingType: MeetingType | null;
  setSelectedMeetingType: SetState<MeetingType | null>;
  meetingType: MeetingType;
}

const NAME = "meetingType";

function MeetingTypeLink({
  meetingType,
  selectedMeetingType,
  setSelectedMeetingType,
}: MeetingTypeLinkProps) {
  const { enabledCoverageRegions } = useMeetingTypeContext();
  const isActive = selectedMeetingType === meetingType;

  const skipLocationForPhone = meetingType === "phone" && !enabledCoverageRegions.phone;
  const skipLocationForVideo = meetingType === "video" && !enabledCoverageRegions.video;
  const canSkipLocationSelection =
    meetingType === "office" || skipLocationForPhone || skipLocationForVideo;

  const className = clsx(
    "group",
    "flex items-center justify-center md:flex-row",
    "px-sm py-xs font-medium",
    "transition-colors hover:border-brand hover:bg-brand hover:text-on-bg hover:drop-shadow-md",
    isActive && "border-brand bg-brand text-white drop-shadow-md",
    !isActive && "select-none border border-gray-300 bg-white text-regular",
    "h-[68px] w-full rounded-sm",
    "md:h-[150px] md:rounded-lg 2xl:h-[175px]",
  );

  const { isLoading, isLoadingForValue } = usePendingNavigation(NAME, meetingType);

  const content = (
    <div
      className="flex place-items-center items-center gap-xs md:grid md:grid-cols-none md:grid-rows-2"
      data-component="MeetingTypeContent"
    >
      {isLoadingForValue ? (
        <Spinner className="h-[32px] w-[32px] fill-brand text-gray-200 group-hover:fill-white group-hover:text-brand" />
      ) : (
        <MeetingTypeIcon className="h-md w-md" meetingType={meetingType} outline />
      )}
      <div className="text-center font-bold md:text-lg" data-component="ButtonLabel">
        <Text i18nKey={`steps.MeetingType.meeting_types.${meetingType}`} />
      </div>
    </div>
  );

  if (canSkipLocationSelection) {
    return (
      <Form method="post">
        <Button
          className={className}
          data-active={isActive}
          data-component="MeetingType"
          data-meeting-type={meetingType}
          isDisabled={isLoading}
          type="submit"
        >
          {content}
        </Button>
        <input name={NAME} type="hidden" value={meetingType}></input>
      </Form>
    );
  }

  return (
    <Button
      className={className}
      data-active={isActive}
      data-component="MeetingType"
      data-meeting-type={meetingType}
      onPress={() => setSelectedMeetingType(meetingType)}
      type="button"
    >
      {content}
    </Button>
  );
}

function MeetingTypeLocation({ meetingType }: { meetingType: MeetingType }) {
  const { t } = useTranslation();
  const { defaultLatLng, enabledCoverageRegions } = useMeetingTypeContext();
  const [selectedLocation, setSelectedLocation] = useState<PlaceDetailLoaderData>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const stateManager = useStateManager();
  const defaultPlaceId =
    stateManager.state.location?.placeId ?? stateManager.state.config.location.placeId;

  const { isLoading, isLoadingForValue } = usePendingNavigation(NAME, meetingType);

  const result = useMemo<
    | { success: false; error: JSX.Element }
    | {
        success: true;
        data: {
          meetingType: MeetingType;
          location?: {
            source: string;
            geolocation?: string;
            formattedAddress?: string;
            placeId?: string;
          };
        };
      }
  >(() => {
    if (defaultPlaceId && selectedLocation && defaultPlaceId === selectedLocation.placeId) {
      return {
        data: { meetingType },
        success: true,
      };
    }

    if (meetingType === "on_location" && !selectedLocation?.isCompletePlaceDetail)
      return {
        success: false,
        error: <Text i18nKey="steps.MeetingType.location.error.incomplete_location" />,
      };

    if (meetingType === "phone" && enabledCoverageRegions.phone && !selectedLocation) {
      return {
        success: false,
        error: <Text i18nKey="steps.MeetingType.location.error.no_location" />,
      };
    }

    if (meetingType === "video" && enabledCoverageRegions.video && !selectedLocation) {
      return {
        success: false,
        error: <Text i18nKey="steps.MeetingType.location.error.no_location" />,
      };
    }

    return {
      data: {
        meetingType,
        location: {
          source: "user",
          geolocation: selectedLocation?.geolocation,
          formattedAddress: selectedLocation?.formattedAddress,
          placeId: selectedLocation?.placeId,
        },
      },
      success: true,
    };
  }, [meetingType, selectedLocation, enabledCoverageRegions, defaultPlaceId]);

  const markers = useMarkers(selectedLocation?.geolocation);

  return (
    <div className="flex w-full flex-col gap-sm">
      <LocationSearch
        defaultPlaceId={defaultPlaceId}
        description={<Trans i18nKey={`steps.MeetingType.location.description.${meetingType}`} />}
        isDisabled={isLoading}
        label={t(`steps.MeetingType.location.label.${meetingType}`)}
        onSelectionChange={(location) => {
          setSelectedLocation(location);
          setIsSubmitted(true);
        }}
        placeholder={t(`steps.MeetingType.location.placeholder.${meetingType}`)}
      />
      <div className="flex w-full items-center justify-end">
        {result.success ? (
          <PrimaryButton isDisabled={isLoading} type="submit">
            {isLoadingForValue ? (
              <div className="flex w-lg justify-center">
                <Spinner size="MEDIUM" theme="white" />
              </div>
            ) : (
              <Text i18nKey="steps.MeetingType.location.confirm_choice" />
            )}
          </PrimaryButton>
        ) : (
          <>
            {isSubmitted && <p className="mr-auto text-sm text-error">{result.error}</p>}
            <PrimaryButton isDisabled>
              <Text i18nKey="steps.MeetingType.location.confirm_choice" />
            </PrimaryButton>
          </>
        )}
      </div>
      {meetingType === "on_location" && (
        <Map
          className="h-[400px] rounded"
          data-component="MeetingTypeMap"
          defaultLocation={defaultLatLng}
          markers={markers}
          zoom={15}
        />
      )}
      {result.success && (
        <div>
          <input name={NAME} type="hidden" value={result.data.meetingType}></input>
          {result.data.location && (
            <>
              <input name="location.source" type="hidden" value={result.data.location.source} />
              {result.data.location.formattedAddress && (
                <input
                  name="location.formattedAddress"
                  type="hidden"
                  value={result.data.location.formattedAddress}
                />
              )}
              {result.data.location.geolocation && (
                <input
                  name="location.geolocation"
                  type="hidden"
                  value={result.data.location.geolocation}
                />
              )}
              {result.data.location.placeId && (
                <input name="location.placeId" type="hidden" value={result.data.location.placeId} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface MeetingTypeIndicatorProps {
  meetingType: MeetingType;
}

export function MeetingTypeIndicator(props: MeetingTypeIndicatorProps) {
  return (
    <span
      className={clsx(
        "flex shrink-0 items-center justify-center",
        "select-none",
        "h-[22px] px-[4px] py-[2px]",
        "text-secondary",
      )}
      data-component="MeetingTypeIndicator"
    >
      <MeetingTypeIcon {...props} height={24} width={24} outline />
    </span>
  );
}

interface MeetingTypeIconProps extends ComponentPropsWithoutRef<"svg"> {
  outline?: boolean;
  meetingType: MeetingType;
}

function MeetingTypeIcon({ meetingType, ...props }: MeetingTypeIconProps) {
  if (meetingType === "office") {
    return <OfficeIcon {...props} />;
  }

  if (meetingType === "video") {
    return <VideoIcon {...props} />;
  }

  if (meetingType === "phone") {
    return <PhoneIcon {...props} />;
  }

  if (meetingType === "on_location") {
    return <LocationIcon {...props} />;
  }

  return null;
}
